import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import AuthService from '../services/authService';
import Spinner from './Spinner';

interface Sport {
  id: number;
  name: string;
  code?: string;
}

interface Team {
  id: number;
  team_name:  string;
  team_code: string | number;
  tidy_name?: string;
  img_url?: string;
  primary_color_code?: string;
  secondary_color_code?: string;
}

// Define interface for the ref
export interface TeamOnboardingFormRef {
  resetForm: () => void;
}

// Convert to forwardRef component
const TeamOnboardingForm = forwardRef<TeamOnboardingFormRef>((props, ref) => {
  // Form state
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamNameQuery, setTeamNameQuery] = useState('');
  const [tidyName, setTidyName] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [primaryColorCode, setPrimaryColorCode] = useState('');
  const [secondaryColorCode, setSecondaryColorCode] = useState('');
  const [selectedSports, setSelectedSports] = useState<number[]>([]);
  
  // UI state
  const [teams, setTeams] = useState<Team[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const authService = new AuthService();

  // Fetch team details when a team is selected
  const fetchTeamDetails = async (teamCode: number | string) => {
    try {
      setError('');
      const response = await authService.getTeamDetails(teamCode);
      
      if (response && response.status === 200 && response.data) {
        const teamDetails = response.data;
        console.log('Team details fetched:', teamDetails);
        
        // Auto-fill form fields with team details
        if (teamDetails.tidy_name) setTidyName(teamDetails.tidy_name);
        if (teamDetails.img_url) setImgUrl(teamDetails.img_url);
        if (teamDetails.primary_color_code) setPrimaryColorCode(teamDetails.primary_color_code);
        if (teamDetails.secondary_color_code) setSecondaryColorCode(teamDetails.secondary_color_code);
        
        // If the team has associated sports, select them
        if (teamDetails.sports && Array.isArray(teamDetails.sports)) {
          const sportIds = teamDetails.sports.map((sport: { id: number }) => sport.id);
          setSelectedSports(sportIds);
        }
      } else {
        console.warn('Failed to fetch team details or empty response', response);
      }
    } catch (err) {
      console.error('Error fetching team details:', err);
      setError('Failed to fetch team details');
    }
  };

  // Fetch sports and teams on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch sports
        const sportsResponse = await authService.getSports();
        console.log('Sports response:', sportsResponse);
        if (sportsResponse && sportsResponse.status === 200) {
          const sportsData = sportsResponse.data || [];
          console.log('Sports data:', sportsData);
          setSports(Array.isArray(sportsData) ? sportsData : []);
        } else {
          setError('Failed to load sports');
          console.error('Failed to load sports:', sportsResponse);
        }

        // Fetch teams
        const teamsResponse = await authService.getAllTeams();
        console.log('Teams response:', teamsResponse);
        if (teamsResponse && teamsResponse.status === 200) {
          const teamsData = teamsResponse.data || [];
          console.log('Teams data:', teamsData);
          setTeams(Array.isArray(teamsData) ? teamsData : []);
        } else {
          setError('Failed to load teams');
          console.error('Failed to load teams:', teamsResponse);
        }
      } catch (err) {
        setError('An error occurred while fetching data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter teams based on query
  const filteredTeams = useMemo(() => {
    if (!teamNameQuery || !teams || !Array.isArray(teams)) return [];
    return teams.filter((team) => 
      team && typeof team === 'object' && team.team_name && 
      typeof team.team_name === 'string' && 
      team.team_name.toLowerCase().includes(teamNameQuery.toLowerCase())
    );
  }, [teams, teamNameQuery]);

  // Handle sport selection
  const handleSportToggle = (sportId: number) => {
    if (!sportId) return;
    
    if (selectedSports.includes(sportId)) {
      setSelectedSports(selectedSports.filter((id) => id !== sportId));
    } else {
      setSelectedSports([...selectedSports, sportId]);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!selectedTeam) {
        setError('Please select a team from the dropdown');
        return;
    }

    if (!tidyName.trim()) {
        setError('Tidy name is required');
        return;
    }
    
    // Validate image URL
    if (!imgUrl.trim()) {
        setError('Logo URL is required');
        return;
    }

    if (selectedSports.length === 0) {
        setError('Please select at least one sport');
        return;
    }

    // Clear previous messages
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
        // Ensure team_code is a valid number
        const teamCodeStr = String(selectedTeam.team_code).trim();
        if (!teamCodeStr || isNaN(Number(teamCodeStr))) {
            setError('Invalid team code format');
            setSubmitting(false);
            return;
        }
        
        // Prepare payload with properly formatted data
        const payload = {
            team_name: selectedTeam.team_name,
            team_code: Number(teamCodeStr), // Ensure it's a number
            tidy_name: tidyName.trim(),
            img_url: imgUrl.trim(),
            primary_color_code: primaryColorCode.trim(),
            secondary_color_code: secondaryColorCode.trim(),
            sports: selectedSports
        };
        
        // Log the payload for debugging
        console.log('Submitting team onboard payload:', payload);
        
        const response = await authService.onboardTeam(payload);

        if (response.status === 200) {
            setSuccess('Team onboarded successfully!');
            
            // Reset form
            resetForm();
        }
    } catch (err: any) {
        console.error('Team onboarding error:', err);
        
        let errorMessage = 'An error occurred while onboarding team';
        
        // Extract the error message from the response
        if (err.response && err.response.data) {
            // Check for detail field (FastAPI HTTPException format)
            if (err.response.data.detail) {
                errorMessage = err.response.data.detail;
            }
            // Check for message field (custom format)
            else if (err.response.data.message) {
                errorMessage = err.response.data.message;
            }
            // Check if data itself is a string
            else if (typeof err.response.data === 'string') {
                errorMessage = err.response.data;
            }
        }
        // Check if error has a message property
        else if (err.message) {
            errorMessage = err.message;
        }
        
        setError(errorMessage);
    } finally {
        setSubmitting(false);
    }
};

  // Add a reset function to the TeamOnboardingForm component
  const resetForm = () => {
    setSelectedTeam(null);
    setTeamNameQuery('');
    setTidyName('');
    setImgUrl('');
    setPrimaryColorCode('');
    setSecondaryColorCode('');
    setSelectedSports([]);
    setError('');
    setSuccess('');
  };

  // Expose resetForm through ref
  useImperativeHandle(ref, () => ({
    resetForm
  }));

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      ) : (
        <>
          {/* Form header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Onboard New Team</h2>
            <p className="text-gray-600">Add a new team to the system</p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Success message */}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Team Name Dropdown */}
              <div>
                <label htmlFor="teamSelect" className="block text-sm font-medium text-gray-700 mb-1">
                  Select Team *
                </label>
                <div className="relative">
                  <div className="flex">
                    <input
                      type="text"
                      id="teamSearch"
                      className="w-full p-2 border rounded-l-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Search for a team"
                      value={teamNameQuery}
                      onChange={(e) => setTeamNameQuery(e.target.value)}
                      autoComplete="off"
                    />
                    <button 
                      type="button"
                      onClick={() => teamNameQuery ? setTeamNameQuery('') : null}
                      className="bg-gray-100 text-gray-700 px-3 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200"
                    >
                      {teamNameQuery ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {teamNameQuery.length > 0 && (
                    <div className="mt-2 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded-md z-10 absolute shadow-lg">
                    {!filteredTeams || filteredTeams.length === 0 ? (
                      <div className="p-2 text-sm text-gray-500">
                        No teams found
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {filteredTeams.map((team) => (
                          <li 
                            key={team.id}
                            className={`p-2 cursor-pointer hover:bg-blue-50 ${selectedTeam?.id === team.id ? 'bg-blue-100' : ''}`}
                            onClick={() => {
                              setSelectedTeam(team);
                              setTeamNameQuery('');
                              fetchTeamDetails(team.team_code);
                            }}
                          >
                            <div className="flex items-center">
                              {selectedTeam?.id === team.id && (
                                <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                </svg>
                              )}
                              <span>{team.team_name}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  )}
                </div>
                {selectedTeam && (
                  <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-gray-700">Selected Team:</p>
                        <p className="text-blue-600">{selectedTeam.team_name}</p>
                        <p className="text-sm text-gray-500">Team Code: <span className="font-mono">{selectedTeam.team_code}</span></p>
                      </div>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="ml-2 p-1 hover:bg-gray-200 rounded-full transition-colors duration-200"
                        title="Clear selection"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 hover:text-gray-700" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Tidy Name - Now editable */}
              <div>
                <label htmlFor="tidyName" className="block text-sm font-medium text-gray-700 mb-1">
                  Tidy Name 
                </label>
                <input
                  id="tidyName"
                  type="text"
                  value={tidyName}
                  onChange={(e) => setTidyName(e.target.value)}
                  className="w-full p-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter the short tidy name(e.g. ARK, LAK, etc.)"
                  required
                />
              </div>

              {/* Image URL */}
              <div>
                <label htmlFor="imgUrl" className="block text-sm font-medium text-gray-700 mb-1">
                  Team Logo URL
                </label>
                <div className="flex items-center gap-4">
                  <input
                    id="imgUrl"
                    type="text"
                    value={imgUrl}
                    onChange={(e) => setImgUrl(e.target.value)}
                    className="w-full p-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter image URL"
                  />
                  {imgUrl && (
                    <img
                      src={imgUrl}
                      alt="Team Logo Preview"
                      className="h-12 w-12 object-contain border rounded bg-white"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                </div>
              </div>

              {/* Primary Color */}
              <div>
                <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    id="primaryColor"
                    type="color"
                    value={primaryColorCode || '#000000'}
                    onChange={(e) => setPrimaryColorCode(e.target.value)}
                    className="w-12 h-10 p-0 border-0"
                  />
                  <input
                    type="text"
                    value={primaryColorCode}
                    onChange={(e) => setPrimaryColorCode(e.target.value)}
                    className="w-full p-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#RRGGBB"
                  />
                </div>
              </div>

              {/* Secondary Color */}
              <div>
                <label htmlFor="secondaryColor" className="block text-sm font-medium text-gray-700 mb-1">
                  Secondary Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    id="secondaryColor"
                    type="color"
                    value={secondaryColorCode || '#FFFFFF'}
                    onChange={(e) => setSecondaryColorCode(e.target.value)}
                    className="w-12 h-10 p-0 border-0"
                  />
                  <input
                    type="text"
                    value={secondaryColorCode}
                    onChange={(e) => setSecondaryColorCode(e.target.value)}
                    className="w-full p-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#RRGGBB"
                  />
                </div>
              </div>
            </div>

            {/* Sports Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sports *
              </label>
              
              {sports.length === 0 ? (
                <p className="text-gray-500">No sports available</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {sports.map(sport => (
                    <div key={sport.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`sport-${sport.id}`}
                        checked={selectedSports.includes(sport.id)}
                        onChange={() => handleSportToggle(sport.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 mr-2"
                      />
                      <label htmlFor={`sport-${sport.id}`} className="text-sm text-gray-700">
                        {sport.name}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
              >
                {submitting ? (
                  <div className="flex items-center">
                    <Spinner />
                    <span className="ml-2">Submitting...</span>
                  </div>
                ) : (
                  'Onboard Team'
                )}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
});

// Export the component with proper typing
export default TeamOnboardingForm;
